'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import Link from '@eliadmin/core/src/components/link';

import { useEffect, useRef, useState, type PropsWithChildren } from 'react';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoIosArrowDown } from 'react-icons/io';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { GoArrowRight } from 'react-icons/go';
import Image from '@eliadmin/core/src/components/image/image-wrapped';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-2', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  title: string;
  href?: string;
  onClick?: () => void;
  order?: number;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const locale = useLocaleStore(s => s.currentLocale);
  const LinkComp = props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const { t } = useTranslations();
  const pathname = usePathname();
  const isCategoriesItem = props.title === t('text150');

  const { data: categories = { data: [] } } = useQuery({
    queryKey: [CacheKey.CATEGORIES, locale],
    enabled: isCategoriesItem,
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          where: { parent: { exists: false } },
          childs: false,
          image: true,
          results: 12,
        },
      }),
  });

  const categoriesSlugs = categories.data.map(service => service.slug);
  const isActive =
    pathname !== '/' &&
    ((isCategoriesItem && categoriesSlugs.some(slug => pathname === `/${slug}`)) ||
      pathname === props.href);

  const handleMouseEnter = () => {
    if (!isCategoriesItem) return;
    setIsMouseOver(true);
    setDropdownVisible(true);
    if (hoverTimeout.current) {
      clearTimeout(hoverTimeout.current);
    }
  };

  const handleMouseLeave = () => {
    if (!isCategoriesItem) return;
    setIsMouseOver(false);
    hoverTimeout.current = setTimeout(() => {
      if (!isMouseOver) {
        setDropdownVisible(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (!isCategoriesItem) return;
    const handleMouseOver = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        (dropdownRef.current.contains(event.target as Node) || event.target === dropdownRef.current)
      ) {
        setIsMouseOver(true);
        setDropdownVisible(true);
        if (hoverTimeout.current) {
          clearTimeout(hoverTimeout.current);
        }
      }
    };

    const handleMouseOut = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        event.target !== dropdownRef.current
      ) {
        handleMouseLeave();
      }
    };

    document.addEventListener('mouseover', handleMouseOver);
    document.addEventListener('mouseout', handleMouseOut);

    return () => {
      document.removeEventListener('mouseover', handleMouseOver);
      document.removeEventListener('mouseout', handleMouseOut);
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, [isMouseOver, isCategoriesItem]);

  useEffect(() => {
    setDropdownVisible(false);
  }, [pathname]);

  return (
    <li
      style={{ order: props.order }}
      className={cn('flex flex-row', 'ea-category-menu-item', props.className?.root)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <LinkComp
        {...(LinkComp === Link ? { prefetchOnHover: true } : {})}
        href={props.href ? getSafeHref(props.href) : ((undefined as any) ?? '#')}
        className={cn(
          'flex items-center gap-2 text-base font-normal uppercase text-foreground',
          'ea-category-menu-item__link',
          isActive && 'font-medium',
          props.className?.link,
        )}
        onClick={props.onClick}
      >
        <span>{props.title}</span>
        {isCategoriesItem && (
          <span className="flex h-4 w-4 shrink-0 items-center justify-center">
            {isCategoriesItem && <IoIosArrowDown className="h-4 w-4 text-primary" />}
          </span>
        )}
      </LinkComp>
      {isCategoriesItem && isDropdownVisible && (
        <>
          <div className="absolute left-0 top-[169px] z-[10] h-screen w-full bg-black/50" />
          <div
            ref={dropdownRef}
            className="absolute left-0 top-[169px] z-40 flex h-fit w-full items-center justify-center bg-white p-4"
          >
            <div className={cn('grid w-full grid-cols-4 gap-4', 'custom-container')}>
              {categories.data.map(category => (
                <Link
                  prefetchOnHover
                  key={category.slug}
                  href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                  className="col-span-1 flex w-full items-center justify-between border border-[#DEDEDED] bg-transparent p-4 text-lg font-semibold uppercase text-foreground transition-all hover:border-primary hover:bg-primary hover:text-white sm:p-5 sm:text-xl xl:p-6"
                >
                  <div className="flex items-center justify-start gap-2">
                    <Image
                      src={category?.image?.url ?? '/images/placeholder.png'}
                      alt={resolveImageAlt(category?.image, category?.name)}
                      width={0}
                      height={0}
                      className="h-12 w-12 xl:h-16 xl:w-16"
                      sizes="64px"
                    />
                    {category.name}
                  </div>

                  <GoArrowRight size={24} />
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </li>
  );
}
